import React, { useMemo } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import { getObjectByKeys } from "helpers/commonHelpers";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import {
  EXPORT_CLEARANCE_OPTIONS,
  IMPORT_CLEARANCE_OPTIONS,
  TRANSIT_CLEARANCE_OPTIONS,
} from "../Service/ServiceFormDefaultOptions";

type ServiceProps = {
  state: RequestFormState;
  isQuoteRequest: boolean;
};

interface Requirement {
  label: string;
  value: string;
  additional_information: string;
}

function Service(props: ServiceProps) {
  const { state, isQuoteRequest } = props;
  const methods = useFormContext();
  const { getValues } = methods;
  const intl = useIntl();
  const [pickupCountry, stop1Country, pickupPortCountry, stop1PortCountry] = useWatch({
    name: [
      "schedule.pickup_country",
      "schedule.stop1_country",
      "schedule.pickup_port_country",
      "schedule.stop1_port_country",
    ],
  });
  const resolvedPickupCountry = pickupCountry || pickupPortCountry;
  const resolvedDeliveryCountry = stop1Country || stop1PortCountry;

  const showClearance = useMemo(
    () =>
      (!isQuoteRequest && state.service?.ask_clearance) ||
      (isQuoteRequest &&
        Boolean(resolvedPickupCountry) &&
        Boolean(resolvedDeliveryCountry) &&
        resolvedPickupCountry !== resolvedDeliveryCountry),
    [resolvedPickupCountry, resolvedDeliveryCountry, isQuoteRequest, state.service],
  );

  const transitClearanceOptions = useMemo<any>(() => getObjectByKeys(TRANSIT_CLEARANCE_OPTIONS(intl), "value"), []);
  const importClearanceOptions = useMemo<any>(() => getObjectByKeys(IMPORT_CLEARANCE_OPTIONS(intl), "value"), []);
  const exportClearanceOptions = useMemo<any>(() => getObjectByKeys(EXPORT_CLEARANCE_OPTIONS(intl), "value"), []);

  return (
    <div className="font-b3">
      <Card className="pl-0 pr-0 mb-30">
        {getValues("service.service_level") && (
          <>
            <Row className="mb-2">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.serviceLevel" defaultMessage="Service level" />
              </Col>
              <Col>{getValues("service.service_level") || "-"}</Col>
            </Row>
            <Row className="mb-2">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.serviceRequirement" defaultMessage="Service requirement" />
              </Col>
              <Col>{getValues("service.request_service_type") || "-"}</Col>
            </Row>
          </>
        )}
        {getValues("service.incoterms") && (
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.incoterms" defaultMessage="Incoterms" />
            </Col>
            <Col>
              {state?.service?.incoterm_values?.find(
                (incoterm: any) => incoterm?.value === getValues("service.incoterms")?.toLowerCase(),
              )?.label || "-"}
            </Col>
          </Row>
        )}
        {getValues("service.incoterm_instructions") && (
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.service.incotermInstructions" defaultMessage="Incoterm instructions" />
            </Col>
            <Col>{getValues("service.incoterm_instructions") || "-"}</Col>
          </Row>
        )}
        {getValues("service.insurance") && (
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="general.insurance" defaultMessage="Insurance" />
            </Col>
            <Col>
              {getValues("service.insurance") ? (
                <FormattedMessage id="general.yes" defaultMessage="Yes" />
              ) : (
                <FormattedMessage id="general.no" defaultMessage="No" />
              )}
              {getValues("service.insurance_instructions") && getValues("service.insurance")
                ? `, ${getValues("service.insurance_instructions")}`
                : ""}
            </Col>
          </Row>
        )}

        {showClearance && (
          <>
            {isQuoteRequest && (
              <div>
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="tableHeaders.exportClearance" defaultMessage="Export clearance" />
                  </Col>
                  <Col>{exportClearanceOptions?.[getValues("service.export_clearance")]?.label || "-"}</Col>
                </Row>
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="quote-request-detail.importClearance" defaultMessage="Import clearance" />
                  </Col>
                  <Col>{importClearanceOptions?.[getValues("service.import_clearance")]?.label || "-"}</Col>
                </Row>
              </div>
            )}

            {getValues("service.transit_clearance") && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="quote-request-detail.transitClearance" defaultMessage="Transit clearance" />
                </Col>
                <Col>{transitClearanceOptions?.[getValues("service.transit_clearance")]?.label || "-"}</Col>
              </Row>
            )}

            {getValues("service.import_clearance_instructions") && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage
                    id="quite-request-detail.importClearanceInstructions"
                    defaultMessage="Import clearance instructions"
                  />
                </Col>
                <Col>{getValues("service.import_clearance_instructions")}</Col>
              </Row>
            )}
            {getValues("service.export_clearance_instructions") && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage
                    id="quote-request-detail.exportClearanceInstructions"
                    defaultMessage="Export clearance instructions"
                  />
                </Col>
                <Col>{getValues("service.export_clearance_instructions") || "-"}</Col>
              </Row>
            )}

            {getValues("service.transit_clearance_instructions") && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage
                    id="quote-request-detail.transitClearanceInstructions"
                    defaultMessage="Transit clearance instructions"
                  />
                </Col>
                <Col>{getValues("service.transit_clearance_instructions") || "-"}</Col>
              </Row>
            )}
          </>
        )}

        <hr />

        {getValues("service.special_requirements") && (
          <>
            {Object.entries<Requirement>(getValues("service.special_requirements")).map(([key, requirement]) => {
              const additionalInformation = requirement.additional_information
                ? `, ${requirement.additional_information}`
                : "";

              return (
                <Row className="mb-2" key={key}>
                  <Col className="text-secondary">{requirement.label}</Col>
                  <Col>
                    <div>{`${requirement.value === "true" ? "Yes" : "No"} ${additionalInformation || ""}`}</div>
                  </Col>
                </Row>
              );
            })}
          </>
        )}
      </Card>
    </div>
  );
}

export default Service;
